import { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import type { SelectOption } from '@/types/common.types';
import type { ConflictInfo, Resource } from '@/services/appointments.service';

export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  doctor: 'Doctors',
  nurse: 'Nurses',
  coordinator: 'Coordinators',
  room: 'Rooms',
  equipment: 'Equipment',
  other: 'Other',
};

export const RESOURCE_TYPE_TO_ROLE_KEY: Record<string, string> = {
  doctor: 'doctors',
  nurse: 'nurses',
  coordinator: 'coordinators',
  room: 'rooms',
  equipment: 'equipment',
  other: 'other',
};

export const RESOURCE_TYPES_ORDERED: ReadonlyArray<Resource['resourceType']> = [
  'doctor',
  'coordinator',
  'nurse',
  'equipment',
  'room',
  'other',
];

export interface ResourceRow {
  key: string;
  /** Null while a row is being set up (user hasn't picked a type yet). */
  resourceType: Resource['resourceType'] | null;
  /** Null until the row is committed to selectedResourceIds. */
  resourceId: number | null;
}

export interface ResourceRowListProps {
  rows: ResourceRow[];
  resources: Resource[];
  resourceTypeLabels: Record<string, string>;
  resourceConflicts?: Record<number, ConflictInfo[]>;
  conflictChecking?: boolean;
  onChangeType: (key: string, resourceType: Resource['resourceType']) => void;
  onChangeResource: (key: string, resourceId: number | null) => void;
  onRemoveRow: (key: string) => void;
  onAddRow: () => void;
  /**
   * Opt-in. When provided, a row that has a category but no resource yet picks
   * several resources at once; each one still lands as its own row. Callers
   * that leave this undefined get the one-resource-per-row picker unchanged.
   */
  onAddResources?: (key: string, resourceIds: number[]) => void;
}

/**
 * The picker shown on a row that has a category but no resource yet, when the
 * caller opted into bulk selection.
 *
 * The chosen ids are staged here and handed over in one call when the panel
 * closes, rather than one at a time as they are ticked. Committed rows are
 * rendered above draft rows, so committing per tick would slide this row —
 * and its open panel — down the list while the user is still picking from it.
 */
function DraftResourcePicker({
  rowKey,
  options,
  onAddResources,
}: {
  rowKey: string;
  options: SelectOption<number>[];
  onAddResources: (key: string, resourceIds: number[]) => void;
}) {
  const [staged, setStaged] = useState<string[]>([]);

  const multiOptions = useMemo(
    () => options.map((o) => ({ value: String(o.value), label: o.label })),
    [options],
  );

  return (
    <SearchableMultiSelect
      options={multiOptions}
      selectedValues={staged}
      onSelectionChange={setStaged}
      placeholder="Select resources…"
      searchPlaceholder="Search resources…"
      emptyMessage="No resources match."
      onOpenChange={(open) => {
        if (open || staged.length === 0) return;
        onAddResources(rowKey, staged.map(Number));
        setStaged([]);
      }}
    />
  );
}

export function ResourceRowList({
  rows,
  resources,
  resourceTypeLabels,
  resourceConflicts = {},
  conflictChecking = false,
  onChangeType,
  onChangeResource,
  onRemoveRow,
  onAddRow,
  onAddResources,
}: ResourceRowListProps) {
  const takenResourceIds = useMemo(
    () => new Set(rows.map((r) => r.resourceId).filter((id): id is number => id != null)),
    [rows],
  );

  const typeOptions: SelectOption<Resource['resourceType']>[] = useMemo(
    () =>
      RESOURCE_TYPES_ORDERED.map((type) => ({
        value: type,
        label: resourceTypeLabels[type] ?? type,
      })),
    [resourceTypeLabels],
  );

  return (
    <div className="space-y-2">
      {rows.length === 0 ? (
        <p className="text-xs italic text-slate-400">
          No resources assigned. Use &quot;Add Resource&quot; to attach a doctor, room, or equipment.
        </p>
      ) : null}
      {rows.map((row, index) => {
        const activeType = row.resourceType;
        const resourceOptions: SelectOption<number>[] = activeType
          ? resources
              .filter((r) => r.active && r.resourceType === activeType)
              .filter((r) => r.resourceId === row.resourceId || !takenResourceIds.has(r.resourceId))
              .map((r) => ({ value: r.resourceId, label: r.resourceName }))
          : [];

        const rowConflicts = row.resourceId ? resourceConflicts[row.resourceId] ?? [] : [];
        const hasConflict = rowConflicts.some((c) => (c.severity ?? 'error') === 'error');
        const hasWarning = !hasConflict && rowConflicts.length > 0;

        return (
          /*
            SCRUM-1408. The four-column track declares 140px + 180px of minimum before
            the three 8px gaps, the 20px status column and the ~24px remove button — a
            little under 390px against the ~343px a 375px phone leaves inside the modal
            padding. The row physically cannot fit, which is why the green dot and the
            ✕ rendered outside the card and the section scrolled sideways.

            Below `md` the two selects stack on their own rows and the status dot and
            remove control share a third, so nothing is pushed off-screen. The
            `md:` track is the original string, unchanged.
          */
          <div
            key={row.key}
            className="grid grid-cols-[minmax(140px,1fr)_minmax(180px,2fr)_auto_auto] gap-2 items-center max-md:grid-cols-[1fr_auto_auto]"
          >
            <Select<Resource['resourceType']>
              options={typeOptions}
              value={activeType ?? null}
              onChange={(next) => {
                if (next) onChangeType(row.key, next);
              }}
              placeholder="Type…"
              id={`resource-row-${index}-type`}
              // Own row below `md`; the resource picker, status dot and remove
              // control then share the row beneath it.
              wrapperClassName="max-md:col-span-3"
            />
            {onAddResources && activeType && row.resourceId == null ? (
              <DraftResourcePicker
                key={`${row.key}-${activeType}`}
                rowKey={row.key}
                options={resourceOptions}
                onAddResources={onAddResources}
              />
            ) : (
              <Select<number>
                options={resourceOptions}
                value={row.resourceId ?? null}
                onChange={(next) => onChangeResource(row.key, next)}
                placeholder={activeType ? 'Select resource…' : 'Pick type first'}
                disabled={!activeType}
                clearable
                searchable
                id={`resource-row-${index}-value`}
              />
            )}
            <div className="flex items-center justify-center w-5" aria-live="polite">
              {row.resourceId == null ? (
                <span className="h-2 w-2 rounded-full bg-slate-200" aria-label="Not yet selected" />
              ) : conflictChecking ? (
                <span
                  className="h-2 w-2 rounded-full bg-slate-300 animate-pulse"
                  aria-label="Checking conflicts"
                />
              ) : hasConflict ? (
                <span
                  className="h-2 w-2 rounded-full bg-red-500"
                  title={`Conflict: ${rowConflicts.map((c) => c.reason ?? c.conflictType).join(', ')}`}
                />
              ) : hasWarning ? (
                <span
                  className="h-2 w-2 rounded-full bg-amber-400"
                  title={`Warning: ${rowConflicts.map((c) => c.reason ?? c.conflictType).join(', ')}`}
                />
              ) : (
                <span className="h-2 w-2 rounded-full bg-emerald-500" title="No conflicts" />
              )}
            </div>
            {/* ~24px before, which is under the touch floor. The shared utilities are
                gated on `(max-width: 1023.98px) and (pointer: coarse)`, so the desktop
                control keeps its current size and the inset keeps the row height. */}
            <button
              type="button"
              onClick={() => onRemoveRow(row.key)}
              className="text-slate-400 hover:text-red-500 transition-colors p-1 touch-target-44 touch-target-inset"
              aria-label="Remove resource"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}

      <div className="pt-1">
        <Button type="button" variant="secondary" size="sm" onClick={onAddRow}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Resource
        </Button>
      </div>
    </div>
  );
}
